import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { arrowRightIcon, arrowLeftIcon } from 'assets';
import { OpDataGrid } from 'op2mise-react-widgets';
import { Modal } from 'react-bootstrap';
import Button from 'shared/components/button/Button';
import { getGroupNameMarkets } from 'components/campaigns/requests';
import { eyeIcon } from 'assets';
import StationAndCableListModal from 'components/markets/components/StationAndCableListModal';
import { saveOrApplyMarkets } from 'components/campaigns/requests';
import NetworkType from 'enums/NetworkTypeEnum';
import StationService from 'components/stations/StationService';
import { useCallbackPrompt } from 'components/_reusable/blocker/UsePromptComponent.ts';
import Dialog from 'components/Dialogs/Dialog';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal';
import { OpButton } from 'op2mise-react-widgets';
import { roundToTwoDecimals } from 'utils/numeric.utils';
import AuthService from 'core/auth.service';
import { USER_TYPE } from 'enums/userRoles';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';

const GroupNameForm = (props) => {
  const {
    onHide,
    show,
    groupName,
    selectedCampaign,
    setGroupNameMarkets,
    marketSpendList,
    setIsFetchingClientGroup,
  } = props;

  const userIdentity = AuthService.getUserIdentity();
  const isOrganisationAdmin = userIdentity.roleId === USER_TYPE.organisationadmin;

  // States

  const [modalSelectedMarket, setModalSelectedMarket] = useState(null);
  const [stations, setStations] = useState([]);

  const [availableMarkets, setAvailableMarkets] = useState([]);
  const [activeMarkets, setActiveMarkets] = useState([]);

  const [toSelectItems, setToSelectItems] = useState([]);
  const [toUnselectItems, setToUnselectItems] = useState([]);

  const [totalMarketSpend, setTotalMarketSpend] = useState('0.00');
  const [isFetchingGroupMarkets, setIsFetchingGroupMarkets] = useState(false);
  const [isSavingMarkets, setIsSavingMarkets] = useState(false);
  const [isApplyLoading, setIsApplyLoading] = useState(false);
  const [isApplyAndSaveLoading, setIsApplyAndSaveLoading] = useState(false);
  const [
    showTotalMarketSpendErrorMessage,
    setShowTotalMarketSpendErrorMessage,
  ] = useState(false);
  const [showMarketSavedDialog, setShowMarketSavedDialog] = useState(false);
  const [marketSpendValues, setMarketSpendValues] = useState([]);

  // Dirty Forms
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    isDirty
  );

  // API Fetch
  const handleGetGroupMarkets = async () => {
    try {
      const payload = {
        campaignId: selectedCampaign.id,
      };

      setIsFetchingGroupMarkets(true);
      const res = await getGroupNameMarkets({
        groupNameId: groupName.id,
        data: JSON.stringify(payload),
      });

      setAvailableMarkets(res?.availableMarkets);
      setActiveMarkets(res?.activeMarkets);

      const spendValues = res?.activeMarkets.map((item) => ({
        id: item.id,
        value: item.allocation,
      }));

      calculateTotalMarketSpend(spendValues);
      setMarketSpendValues(spendValues);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetchingGroupMarkets(false);
    }
  };

  const handleGetStations = async (market) => {
    const response = await StationService.getActiveStations(market?.id, {
      organisationId: selectedCampaign?.organisationId,
      clientId: selectedCampaign?.clientId,
      campaignType: selectedCampaign?.type,
    });

    const stations = response.data.stations.map((data) => ({
      id: data.id,
      code: data.code,
      network: data.network,
      name: data.name,
      target: data.target,
      networkType: NetworkType[data.networkType],
    }));

    setStations(stations);
  };

  const handleOnChangeDefaultSpend = (value, params) => {
    setMarketSpendValues((prevState) => {
      const newMarketSpendValue = prevState.map((item) => {
        if (item.id === params.data.id) {
          return { ...item, value };
        }
        return item;
      });
      return newMarketSpendValue;
    });
  };

  const calculateTotalMarketSpend = (data) => {
    const listOfMarketSpend = data
      .filter((item) => item.value)
      .map((item) => item.value);
    const sum = listOfMarketSpend.reduce(
      (accumulator, currentValue) =>
        parseFloat(accumulator) + parseFloat(currentValue),
      0
    );
    const roundedSum = sum.toFixed(2);
    setTotalMarketSpend(roundedSum);
  };

  const handleOnSaveAndProceed = () => {
    handleSaveActiveMarkets(false);
  };

  const handleOnDiscard = () => {
    confirmNavigation();
    onHide();
  };

  const handleOnHide = () => {
    if (showConfirmLeaveModal) setShowConfirmLeaveModal(false);
    else cancelNavigation();
  };

  const handleOnClose = (ignoreDirtyForm) => {
    if (isDirty && !ignoreDirtyForm) {
      setShowConfirmLeaveModal(true);
    } else {
      onHide();
    }
  };

  // Op grid settings
  const handleOnCellKeyDown = (params) => {
    // event handler for inputs in textfields
    const { column, event, data, node } = params;
    if (
      (event.target.tagName === 'INPUT' || event.target.type === 'number') &&
      column.colId === 'allocation'
    ) {
      // regex that only allows inputs of 2 digits or '100'
      const regex = /100|[0-9]{0,3}/;
      if (event.target.value > 999.99) {
        const match = event.target.value.match(regex);
        event.target.value = parseFloat(match);
      }
      // if input is null or undefined, return 0
      if (!event.target.value) {
        event.target.value = 0;
      }
      // match an input with a 0 followed by a digit, but not a 0
      // followed by a decimal point
      if (event.target.value.match(/^0\d(?!\.)/)) {
        event.target.value = event.target.value.split('')[1];
      }
      // checks if input is more than 1 zero
      if (event.target.value.match(/^00+/)) {
        // replaces multiple zeroes with 1 zero
        event.target.value = event.target.value.replace(/^00+/, 0);
      }

      const newAllocation = parseFloat(event.target.value);

      node.setData({
        ...data,
        allocation: roundToTwoDecimals(newAllocation),
      });
    }
  };

  const handleOnCellEditingStopped = useCallback(
    (params) => {
      if (params.column.colId === 'allocation') {
        let newTotalMarketSpend = 0;

        params.api.forEachNode((node) => {
          newTotalMarketSpend += parseFloat(node.data.allocation || 0);
        });

        handleOnChangeDefaultSpend(params.data.allocation, params);
        setTotalMarketSpend(newTotalMarketSpend);
      }
    },
    [handleOnChangeDefaultSpend]
  );

  // Datagrid Action
  const handleOnAddAllItems = () => {
    setActiveMarkets((prev) => [...prev, ...availableMarkets]);
    setAvailableMarkets([]);
    setIsDirty(true);
  };

  const handleOnAddSelected = () => {
    let selectedMarket = toSelectItems.map((item) => item.data);
    let newActiveMarkets = [...selectedMarket, ...activeMarkets];

    let newAvailableMarkets = availableMarkets.filter(
      (market) => !selectedMarket.some((selected) => selected.id === market.id)
    );
    setAvailableMarkets(newAvailableMarkets);
    setActiveMarkets(newActiveMarkets);
    setIsDirty(true);
  };

  const handleOnRemoveAll = () => {
    setAvailableMarkets((prev) => [...prev, ...activeMarkets]);
    setActiveMarkets([]);
    setIsDirty(true);
  };

  const handleOnRemoveSelected = () => {
    const selectedActiveMarkets = toUnselectItems.map((item) => item.data);
    let newActiveMarkets = activeMarkets.filter(
      (market) =>
        !selectedActiveMarkets.some((selected) => selected.id === market.id)
    );
    setActiveMarkets(newActiveMarkets);
    setAvailableMarkets((prev) => [...prev, ...selectedActiveMarkets]);
    setIsDirty(true);
  };

  useEffect(() => {
    const spendValues = activeMarkets.map((item) => ({
      id: item.id,
      value: item.allocation,
    }));
    calculateTotalMarketSpend(spendValues);
    setMarketSpendValues(spendValues);
  }, [activeMarkets]);

  const handleViewMarket = async (e, market) => {
    e.stopPropagation();

    await handleGetStations(market);

    setModalSelectedMarket(market);
  };

  const onHideStationAndCablesModal = () => {
    setModalSelectedMarket();
    setStations([]);
  };

  // Modal Actions
  const handleSaveActiveMarkets = async (isSave) => {
    if (!isSave) {
      setIsApplyLoading(true);
    } else {
      setIsApplyAndSaveLoading(true);
    }
    setIsSavingMarkets(true);
    setIsFetchingClientGroup(true);
    try {
      if (parseFloat(totalMarketSpend).toFixed(2) >= 0) {
        const parameters = marketSpendValues.map((item) => ({
          id: item.id,
          allocation: item.value ? parseFloat(item.value).toFixed(2) : 0,
        }));

        const newActiveMarkets = activeMarkets.map(({ active, ...rest }) => {
          const match = parameters.find((item) => item.id === rest.id) || {};
          return { ...rest, ...match };
        });

        const payload = {
          save: isSave,
          campaignId: selectedCampaign?.id,
          markets: newActiveMarkets,
        };

        const res = await saveOrApplyMarkets({
          groupNameId: groupName?.id,
          data: JSON.stringify(payload),
        });

        setGroupNameMarkets(res?.clientGroupMarkets);

        setMarketSpendValues([]);
        setShowTotalMarketSpendErrorMessage(false);
        if (showPrompt) confirmNavigation();
        setIsDirty(false);
        onHide();
      } else {
        cancelNavigation();
        setShowTotalMarketSpendErrorMessage(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingMarkets(false);
      setIsApplyLoading(false);
      setIsApplyAndSaveLoading(false);
      setIsFetchingClientGroup(false);
    }
  };

  const leftGridColumnDefs = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'NAME',
        sort: 'asc',
      },
    ];
  }, []);

  const rightGridColumnDefs = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'NAME',
        sort: 'asc',
      },
      {
        cellClass: () => 'ag-cell-enabled ag-input-text-align-right',
        cellEditor: 'agNumberCellEditor',
        cellDataType: 'number',
        cellEditorParams: {
          precision: 2, // allows only up to decimal place inputs
          min: 0,
          max: 999.99,
        },
        cellRenderer: (params) => {
          return (
            <span className="ag-input-cell-renderer">
              {params.value ? params.value : 0}
            </span>
          );
        },
        editable: () => true,
        field: 'allocation',
        filter: true,
        sortable: true,
        headerName: 'DEFAULT SPEND (%)',
      },
      {
        cellClass: 'ag-text-align-center',
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        cellRenderer: (params) => {
          return (
            <span style={{ cursor: 'pointer' }}>
              <img
                src={eyeIcon}
                title={'View stations and cables'}
                alt={'view-stations-and-cables'}
                onClick={(e) => {
                  params.node.setSelected(false);
                  handleViewMarket(e, params.data);
                }}
              />
            </span>
          );
        },
        field: '',
        headerName: ' ',
        resizable: false,
        width: 80,
      },
    ];
  }, [handleViewMarket]);

  useEffect(() => {
    handleGetGroupMarkets();
  }, []);

  return (
    <Modal
      centered
      onHide={() => handleOnClose(false)}
      show={show}
      dialogClassName="group-name-modal"
    >
      <div className="group-name-modal-wrapper">
        {isFetchingGroupMarkets ? (
          <BootstrapSpinner />
        ) : (
          <>
            <p className="group-name-modal-title">{groupName.name}</p>
            {showTotalMarketSpendErrorMessage && (
              <span className="text-end total-market-spend-label d-flex justify-content-end">
                Total market spend: {parseFloat(totalMarketSpend).toFixed(2)}%
              </span>
            )}
            <div>
              <OpDataGrid
                actions={[
                  {
                    disabled: !availableMarkets.length,
                    icon: arrowLeftIcon,
                    iconPosition: 'right',
                    label: 'Add all',
                    onClick: handleOnAddAllItems,
                  },
                  {
                    disabled: !toSelectItems.length,
                    label: 'Add Selected',
                    icon: arrowLeftIcon,
                    iconPosition: 'right',
                    onClick: handleOnAddSelected,
                  },
                  {
                    disabled: !toUnselectItems.length,
                    icon: arrowRightIcon,
                    iconPosition: 'left',
                    label: 'Remove Selected',
                    onClick: handleOnRemoveSelected,
                  },
                  {
                    disabled: !activeMarkets.length,
                    icon: arrowRightIcon,
                    iconPosition: 'left',
                    label: 'Remove all',
                    onClick: handleOnRemoveAll,
                  },
                ]}
                alignedGrid={false}
                dualGridProps={{
                  leftGrid: {
                    columns: leftGridColumnDefs,
                    customVerbiage: 'No markets found',
                    defaultSort: {
                      field: 'name',
                      sort: 'asc',
                    },
                    enableMultipleRowSelection: true,
                    gridHeightBuffer: 600,
                    gridLabel: 'Available markets:',
                    onSelectionChanged: (params) => setToSelectItems(params),
                    resizable: true,
                    rows: availableMarkets,
                    sharedColumnProperties: {
                      filter: { fields: ['name', 'allocation'], value: true },
                      flex: { fields: ['name', 'allocation'], value: 1 },
                      sortable: { fields: ['name', 'allocation'], value: true },
                    },
                    showAlternativeRowHighlight: true,
                    showRowSelectionCount: true,
                    suppressRowClickSelection: false,
                  },
                  rightGrid: {
                    columns: rightGridColumnDefs,
                    customVerbiage: 'Please select and add a market',
                    defaultSort: {
                      field: 'name',
                      sort: 'asc',
                    },
                    enableMultipleRowSelection: true,
                    gridLabel: 'Selected markets:',
                    gridLabel: (
                      <div className="d-flex justify-content-between align-items-end">
                        <span>Selected markets: </span>
                        <div className="d-flex justify-content-end flex-column">
                          <span>
                            {showTotalMarketSpendErrorMessage ? (
                              <span className="text-end total-market-spend-error-message d-flex justify-content-end">
                                Total should equal 100%
                              </span>
                            ) : (
                              <span className="text-end total-market-spend-label d-flex justify-content-end">
                                Total market spend: {totalMarketSpend}%
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    ),
                    onCellEditingStopped: handleOnCellEditingStopped,
                    onCellKeyDown: handleOnCellKeyDown,
                    onSelectionChanged: setToUnselectItems,
                    resizable: true,
                    rows: activeMarkets,
                    sharedColumnProperties: {
                      filter: { fields: ['name', 'allocation'], value: true },
                      flex: { fields: ['name', 'allocation'], value: 1 },
                      sortable: { fields: ['name', 'allocation'], value: true },
                    },
                    showAlternativeRowHighlight: true,
                    showRowSelectionCount: true,
                    suppressRowClickSelection: false,
                  },
                }}
                mode={'DUAL'}
              />
            </div>

            {modalSelectedMarket && (
              <StationAndCableListModal
                list={stations}
                marketName={modalSelectedMarket.name}
                onHide={onHideStationAndCablesModal}
                show={!!modalSelectedMarket}
              />
            )}

            <ConfirmLeaveModal
              show={showPrompt || showConfirmLeaveModal}
              onHide={handleOnHide}
              onSave={handleOnSaveAndProceed}
              onDiscard={handleOnDiscard}
            />

            <div className="align-items-center d-flex justify-content-end">
              <Button
                defaultBtn
                onClick={() => handleOnClose(false)}
                text="Cancel"
              />
              <OpButton
                onClick={() => handleSaveActiveMarkets(false)}
                label={'Apply'}
                disabled={isSavingMarkets}
                loading={isApplyLoading}
              />

              {isOrganisationAdmin && (
                <OpButton
                  label={'Apply and Save Group'}
                  style={{ marginLeft: '20px' }}
                  onClick={() => handleSaveActiveMarkets(true)}
                  disabled={isSavingMarkets}
                  loading={isApplyAndSaveLoading}
                />
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default GroupNameForm;
